// Shared navigation model for the documentation page.
//
// The docs page renders every section stacked in one long-scroll column and
// uses a scroll-spy sidebar. GROUPS is the single source of truth for the
// section order + labels; the diagrams (architecture, run lifecycle, ERD)
// import SectionId + scrollToDocsSection so a clicked node can jump straight
// to the relevant section.

export const GROUPS = [
  {
    label: 'Introduction',
    sections: [
      { id: 'overview', label: 'Overview' },
      { id: 'getting-started', label: 'Getting Started' },
    ],
  },
  {
    label: 'Architecture',
    sections: [
      { id: 'architecture', label: 'The Big Picture' },
      { id: 'data-model', label: 'Data Model' },
      { id: 'run-lifecycle', label: 'Anatomy of a Run' },
    ],
  },
  {
    label: 'Working with agents',
    sections: [
      { id: 'projects', label: 'Projects' },
      { id: 'issues', label: 'Issues & Briefs' },
      { id: 'runs', label: 'Runs & Monitoring' },
      { id: 'loops', label: 'Scheduled Loops' },
      { id: 'prototypes', label: 'Prototypes' },
    ],
  },
  {
    label: 'The loop',
    sections: [
      { id: 'agent-roles', label: 'Agent Roles' },
      { id: 'loop-gates', label: 'Gates & Retries' },
      { id: 'iteration', label: 'Iterating on Runs' },
      { id: 'automerge', label: 'Auto-merge & Risk' },
    ],
  },
  {
    label: 'Monitoring',
    sections: [
      { id: 'activity', label: 'Activity Monitor' },
      { id: 'metrics', label: 'Metrics' },
    ],
  },
  {
    label: 'Configuration',
    sections: [
      { id: 'settings', label: 'Settings & Credentials' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'security', label: 'Security (2FA)' },
      { id: 'integration-api', label: 'Integration API' },
    ],
  },
] as const;

export type SectionId = (typeof GROUPS)[number]['sections'][number]['id'];

export const SECTION_IDS: readonly SectionId[] = GROUPS.flatMap((g) =>
  g.sections.map((s) => s.id),
);

export function isSectionId(value: string | null | undefined): value is SectionId {
  return value != null && (SECTION_IDS as readonly string[]).includes(value);
}

// Sticky header (56px) + breathing room. Must match the `scroll-mt-24` on each
// <section> so smooth-scroll targets land below the fixed header, not under it.
export const SCROLL_OFFSET = 96;

/**
 * Smooth-scroll the docs column to a section and reflect it in the URL hash
 * (via replaceState so it doesn't spam the history stack or trigger a router
 * navigation). Safe to call from diagram node handlers.
 */
export function scrollToDocsSection(id: SectionId | string) {
  const el = typeof document !== 'undefined' ? document.getElementById(id) : null;
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (typeof history !== 'undefined' && typeof history.replaceState === 'function') {
    history.replaceState(null, '', `#${id}`);
  }
}
